import clsx from 'clsx';
import { RefObject } from 'preact';
import { useEffect, useId, useState } from 'preact/hooks';
import { useDispatch } from 'react-redux';

import { createSelectProductsPromptModalState } from '@/components/HabitatLink/utils';
import { useHabitatLiveStreamPlayerContext } from '@/components/HabitatLiveStreamPlayer/contexts';
import CameraSelector from '@/components/HabitatLiveStreamPlayer/Controls/CameraSelector';
import { zlSwitchCamera } from '@/components/icons';
import StreamControls from '@/components/LiveStream/StreamInteractiveArea/StreamControls';
import { useLiveStreamPlayerStateContext } from '@/components/LiveStreamPlayer/contexts';
import LiveStreamPlayerControlsFull from '@/components/LiveStreamPlayer/LiveStreamPlayerControls/Full';
import { setMembersOnlyWarningModalState } from '@/components/MembersOnlyWarningModal/redux/actions';
import { setProductsPromptModalState } from '@/components/products/ProductsPromptModal/redux/actions';
import SwitchControl from '@/components/SwitchControl';
import { FullscreenControlButton, IconButton, SoundControlButtonGroup } from '@/components/VideoControls/buttons';
import useFullscreenScale from '@/hooks/useFullscreenScale';
import { useSelector, useStore } from '@/redux/helper';
import { selectIsFreeUser } from '@/redux/selectors/users';

import BackgroundMusicSelector from '../BackgroundMusicSelector';

interface HomeLiveStreamPlayerDesktopControlsProps {
  containerRef: RefObject<HTMLDivElement>;
  miniMode?: boolean;
  isAmbientMode?: boolean;
  isMuted: boolean;
  volume: number;
  onAmbientModeChange?: (isAmbientMode: boolean) => void;
  onMutedChange?: (isMuted: boolean) => void;
  onPlayingChange?: (isPlaying: boolean) => void;
  onVolumeChange: (volume: number) => void;
}

const HomeLiveStreamPlayerDesktopControls = ({
  containerRef,
  miniMode,
  isMuted,
  onMutedChange,
  onPlayingChange,
  isAmbientMode,
  onAmbientModeChange,
  volume,
  onVolumeChange,
}: HomeLiveStreamPlayerDesktopControlsProps) => {
  const id = useId();
  const store = useStore();
  const isFreeUser = useSelector(selectIsFreeUser);
  const logged = useSelector((state) => state.user.logged);
  const dispatch = useDispatch();
  const [isMinimized, setIsMinimized] = useState(true);
  const { scaleClassName: fullscreenControlsScaleClassName } = useFullscreenScale(containerRef);
  const { isVideoLoaded, requestFullscreenFromIFrame } = useLiveStreamPlayerStateContext();
  const { habitat } = useHabitatLiveStreamPlayerContext();
  const hasAmbientModeBackgroundMusics = useSelector((state) => state.remoteConfig.ambientModeBackgroundMusics.length > 0);

  // auto minimize the controls when the user is not interacting with the container
  useEffect(() => {
    const div = containerRef.current;
    if (!div) {
      return undefined;
    }

    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let lastTouchEndTime: number | null = null;

    const handleMouseEnter = () => {
      setIsMinimized(false);
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    };

    const handleMouseLeave = () => {
      // Sometimes the mouse leave event is triggered after the touch end event,
      // so we ignore the mouse leave event if the touch end event happened within the last 300ms and the timeout is still active
      if (lastTouchEndTime && Date.now() - lastTouchEndTime < 300 && timeoutId) {
        return;
      }
      setIsMinimized(true);
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    };

    const handleMouseMove = () => {
      setIsMinimized(false);
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      timeoutId = setTimeout(() => {
        setIsMinimized(true);
      }, 3000);
    };

    const handleTouchEnd = () => {
      lastTouchEndTime = Date.now();
      handleMouseMove(); // we want to treat touch end as a mouse move to keep the controls visible for a few seconds
    };

    div.addEventListener('touchend', handleTouchEnd);
    div.addEventListener('mouseenter', handleMouseEnter);
    div.addEventListener('mouseleave', handleMouseLeave);
    div.addEventListener('mousemove', handleMouseMove);

    return () => {
      div.removeEventListener('touchend', handleTouchEnd);
      div.removeEventListener('mouseenter', handleMouseEnter);
      div.removeEventListener('mouseleave', handleMouseLeave);
      div.removeEventListener('mousemove', handleMouseMove);

      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [containerRef]);

  return (
    <div
      data-minimized={isMinimized}
      className={clsx('group/desktop-controls absolute inset-0 data-[minimized=true]:cursor-none', miniMode && '!hidden')}
    >
      {isAmbientMode && (
        <BackgroundMusicSelector
          volume={volume}
          muted={isMuted}
          isExpanded={!isMinimized}
          className={clsx(
            'absolute bottom-2 left-4 transition-transform',
            fullscreenControlsScaleClassName
              ? 'group-data-[minimized=false]/desktop-controls:-translate-y-16'
              : 'group-data-[minimized=false]/desktop-controls:-translate-y-8',
            fullscreenControlsScaleClassName && `origin-bottom-left ${fullscreenControlsScaleClassName}`,
          )}
        />
      )}
      <LiveStreamPlayerControlsFull.Container
        className={clsx(
          'bg-transparent bg-gradient-to-t from-black/25 to-transparent px-2 transition-transform group-data-[minimized=false]/desktop-controls:translate-y-0',
          // When ButtonsGroup is scaled 2x, translate by 200% to fully hide the scaled content
          fullscreenControlsScaleClassName ? 'translate-y-[200%]' : 'translate-y-full',
        )}
      >
        <LiveStreamPlayerControlsFull.ButtonsGroup
          className={clsx(fullscreenControlsScaleClassName && `origin-bottom-left ${fullscreenControlsScaleClassName}`)}
        >
          <LiveStreamPlayerControlsFull.PlayPauseButton disabled={!isVideoLoaded} onValueChange={onPlayingChange} />
          <SoundControlButtonGroup
            orientation="vertical"
            disabled={!isVideoLoaded && !isAmbientMode}
            volume={volume}
            onVolumeChange={onVolumeChange}
            isMuted={isMuted}
            onMutedChange={onMutedChange}
            rangeInputContainerClassName="bg-grey-1 shadow-none"
          />
          <FullscreenControlButton fullscreenElementRef={containerRef} onFailToFullscreen={requestFullscreenFromIFrame} />
          <CameraSelector
            renderButton={({ onClick }) => <IconButton label="Switch camera" icon={zlSwitchCamera} onClick={onClick} />}
          />
        </LiveStreamPlayerControlsFull.ButtonsGroup>
      </LiveStreamPlayerControlsFull.Container>
      {isAmbientMode != null && hasAmbientModeBackgroundMusics && (
        <div
          className={clsx(
            'absolute',
            fullscreenControlsScaleClassName ? 'bottom-6 right-10' : 'bottom-2 right-4',
            fullscreenControlsScaleClassName && `origin-bottom-right ${fullscreenControlsScaleClassName}`,
          )}
        >
          <div className="inline-flex flex-row items-center gap-2 rounded-full bg-white/60 px-2.5 py-1 backdrop-blur-[3px]">
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label htmlFor={`${id}-ambient-mode`} className="ml-1 text-xs font-bold">
              Ambiance Mode
            </label>
            <SwitchControl
              id={`${id}-ambient-mode`}
              className="aria-checked:bg-[#C89400]"
              size="small"
              checked={isAmbientMode}
              onChange={onAmbientModeChange}
            />
          </div>
        </div>
      )}
      {(!logged || isFreeUser) && (
        <div
          onClickCapture={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (logged) {
              const defaultState = createSelectProductsPromptModalState(habitat._id)(store.getState());
              dispatch(
                setProductsPromptModalState({
                  ...defaultState,
                  entryPoint: 'home-player-fake-controls',
                }),
              );
            } else {
              dispatch(
                setMembersOnlyWarningModalState({
                  type: 'guest-general',
                  redirectOnDismiss: false,
                }),
              );
            }
          }}
        >
          <StreamControls />
        </div>
      )}
    </div>
  );
};

export default HomeLiveStreamPlayerDesktopControls;
